import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowRight, faEnvelope, faPlay } from '@fortawesome/free-solid-svg-icons';
import { faGithub, faLinkedin } from '@fortawesome/free-brands-svg-icons';
import './Home.css';

import profilePic from '../assets/images/NewProfile.png';
import paperImgPreview from '../assets/images/paper.png';
import finalSystemImgPreview from '../assets/images/multimeter.jpg';
import captureDataImgPreview from '../assets/images/capture-data.png';
import subscriptionsPageImgPreview from '../assets/images/Subscriptions-page.png';
import quickPollPreviewImg from '../assets/images/MongoDB.png';
import preview from '../assets/images/preview.png';
import waterlooHero from '../assets/images/WATERLOO.png';
import arcticWolfImg from '../assets/images/ArcticWolf.jpeg';
import arcticWolfImg2 from '../assets/images/ArcticWolf2.png';
import 'devicon/devicon.min.css';
import technicalSkills from '../data/technicalSkills';

// Samskrita OCR images
import samskritaImg1 from '../assets/images/1.png';
import samskritaImg2 from '../assets/images/2.png';
import samskritaImg3 from '../assets/images/3.png';
import samskritaImg4 from '../assets/images/4.png';
import samskritaImg5 from '../assets/images/5.png';

// Computer Club & ArrowHack
import computerClubImg from '../assets/images/computer-club.png';
import hackathonImg from '../assets/images/hackathon.png';

// Samsung Electronics
import samsungOfficeImg from '../assets/images/Samsung_Office.png';
import samsungDeskImg from '../assets/images/Samsung_Desk.png';

// RBC Borealis / WhaleGuard
import rbcBorealisImg1 from '../assets/images/RBC_Borealis1.png';
import rbcBorealisImg2 from '../assets/images/RBC_Borealis2.png';

// Accurate Heart
import accurateHeart1 from '../assets/images/Accurate_Heart1.png';
import accurateHeart2 from '../assets/images/Accurate_Heart2.png';

const getLevelColor = (level) => {
    const colors = {
        'Expert': '#22c55e',
        'Advanced': '#f59e0b',
        'Intermediate': '#06b6d4'
    };
    return colors[level] || '#64748b';
};

// ── Experiences (newest → oldest) ──────────────────────────────────────────
const experiences = [
    {
        id: 10,
        title: "Data Engineering Co-op",
        company: "Samsung Electronics Canada · Co-op",
        period: "May 2026 – Aug 2026",
        location: "Mississauga, Ontario, Canada",
        description: "Architected and deployed a production Python data pipeline on the Network Sales team, transforming unstructured telemetry from the USM database into automated financial reports for enterprise telecom clients — Telus, Videotron, and SaskTel. Discovered and resolved a critical MFBI cell deduplication flaw that prevented millions of dollars in recurring overbilling, directly safeguarding client trust at scale.",
        skills: ["Python (Programming Language)", "Data Engineering", "Pandas", "ETL Pipeline", "Excel Automation", "Data Validation", "Problem Solving", "Telecommunications", "Financial Analytics"],
        isProject: true,
        sections: [
            {
                title: "Samsung Electronics Canada — Crossroads Corporate Centre",
                description: "Embedded on the Network Sales team at Samsung's Canadian headquarters in Mississauga, I worked with enterprise-grade telecom data spanning thousands of cell sites across Canada. The role required rapidly ramping up on USM telemetry schemas, understanding how BBU, RRU, and vDU hardware units map to financial billing events, and designing automation that non-technical finance and sales teams could actually rely on.",
                image: samsungOfficeImg
            },
            {
                title: "Engineering the Pipeline & Resolving a Multi-Million Dollar Flaw",
                description: "The legacy snapshot-based system had a fundamental flaw: temporary network drop-offs were being double-counted as new cell activations, producing inaccurate billing reports. I engineered a completely new Python pipeline using default dictionaries and sets of tuples to fingerprint each physical cell site across dates. I also designed custom MFBI deduplication logic that correctly aggregated distinct frequency bands (e.g., N77 and N78) sharing the same Network Element ID — discovering and fixing an edge case that would have resulted in massive recurring overbilling across enterprise accounts. These automated Excel outputs fed directly into the sales and finance teams' billing workflows.",
                image: samsungDeskImg
            }
        ]
    },
    {
        id: 11,
        title: "ML Research Engineer – WhaleGuard",
        company: "RBC Borealis · Let's SOLVE It Mentorship Program",
        period: "Mar 2026 – May 2026",
        location: "Toronto, Ontario, Canada",
        description: "Building a predictive species distribution model for critically endangered North Atlantic Right Whales (~380 remaining) as part of team Neural Network Navigators in the Spring 2026 cohort. Engineered a 4-phase ML pipeline achieving 0.91 ROC-AUC with Random Forest on 64,900 samples, enabling proactive vessel rerouting and speed restrictions before collisions occur.",
        skills: ["Python (Programming Language)", "Machine Learning", "XGBoost", "Random Forest", "scikit-learn", "Pandas", "Data Engineering", "SHAP", "Streamlit", "OPeNDAP", "BayesSearchCV"],
        isProject: true,
        sections: [
            {
                title: "Team Neural Network Navigators — RBC Borealis Program",
                description: "Selected for the RBC Borealis Let's SOLVE It Undergraduate Mentorship Program (Spring 2026 cohort) alongside teammates Parth and Nikhil. The program pairs undergraduate teams with RBC AI mentors to tackle real-world conservation challenges. Our project, WhaleGuard, addresses one of Canada's most urgent conservation crises: the North Atlantic Right Whale, with fewer than 380 individuals remaining, faces ship strikes as a primary cause of mortality. Current solutions are reactive — we built a predictive system instead.",
                image: rbcBorealisImg1
            },
            {
                title: "Presenting WhaleGuard — Live NARW Habitat Heatmap",
                description: "We presented our finalized Streamlit dashboard to RBC Borealis mentors and other program teams, showcasing live habitat probability queries and a Gulf of St. Lawrence heatmap that updates based on real satellite oceanographic inputs. The model — trained on 51,920 samples and tested on 12,981 — achieved 0.91 ROC-AUC with Random Forest and ≥80% recall at a conservation-optimized threshold (τ = 0.15), prioritizing whale detection over false-positive minimization. Our two-stage optimization first maximized ranking quality via BayesSearchCV, then tuned thresholds to satisfy the conservation recall constraint.",
                image: rbcBorealisImg2
            }
        ]
    },
    {
        id: 9,
        title: "Developer Intern",
        company: "Arctic Wolf · Co-op",
        period: "Sep 2025 – Dec 2025",
        location: "Remote",
        description: "Worked on the Alaska Engineering team in a production cloud and DevOps environment. Contributed to infrastructure migration to Microsoft Azure, stabilized CI/CD test pipelines in containerized environments, and investigated integration test failures across production and staging systems using Python, Pytest, and Terragrunt.",
        skills: ["Amazon Web Services (AWS)", "Jenkins", "Terragrunt", "pytest", "Docker", "Terraform", "Amazon ECS", "Amazon DynamoDB", "Python (Programming Language)", "Problem Solving", "Slack", "Jira", "Documentation"],
        isProject: true,
        sections: [
            {
                title: "Cloud Migration, CI/CD, and Reliability Engineering",
                description: "During my Arctic Wolf co-op, I supported the migration of legacy infrastructure to Microsoft Azure by managing virtual machines and automating agent deployment with PowerShell and Azure File Share. I configured and maintained Jenkins jobs used to validate services across production and staging environments, and investigated integration test failures by debugging services locally with Python and Pytest. I extended and stabilized Pytest-based test suites in containerized CI/CD workflows to improve reliability, while gaining hands-on exposure to AWS and Terragrunt through infrastructure-as-code repositories.",
                image: arcticWolfImg
            },
            {
                title: "Working at Arctic Wolf",
                description: "Arctic Wolf is a cybersecurity leader delivering managed detection and response at enterprise scale. Working on the Alaska Engineering team exposed me to how mission-critical cloud infrastructure is maintained, tested, and evolved without downtime. The role demanded careful attention to cross-environment compatibility, detailed documentation, and rigorous testing discipline in a codebase used to protect thousands of organizations.",
                image: arcticWolfImg2
            }
        ]
    },
    {
        id: 1,
        title: "Web Developer",
        company: "SAMSKRITA BHARATI, USA · Co-op",
        period: "Jan 2025 – Apr 2025",
        location: "Brampton, Ontario, Canada · Remote",
        description: "Co-built a production OCR web service from scratch with a fellow Waterloo co-op student, with minimal guidance. Deployed to AWS EC2 with Nginx reverse proxy and SSL, serving real users. Integrated Google Cloud Vision and Azure Computer Vision APIs, Auth0 authentication, and AWS S3 + DynamoDB for storage at scale.",
        skills: ["React.js", "Optical Character Recognition (OCR)", "Express.js", "Cascading Style Sheets (CSS)", "HTML5", "JavaScript", "Node.js", "Full-Stack Development", "REST APIs", "Amazon Web Services (AWS)", "Google Cloud Platform (GCP)", "Microsoft Azure", "Digital Authentication", "User-centered Design", "Team Collaboration", "Initiative", "Visual Web Developer"],
        isProject: true,
        sections: [
            {
                title: "Initial Website Development and API Integration",
                description: "Collaborated with a University of Waterloo peer to build an internal OCR service from scratch — homepage, OCR processing page, and history feature. Implemented the Express.js backend integrating Google Cloud Vision API for text extraction, file uploads with Multer, image storage in AWS S3, and metadata in DynamoDB.",
                image: samskritaImg1
            },
            {
                title: "Cloud Deployment and Infrastructure Setup",
                description: "Transitioned from localhost to a production AWS EC2 instance. Configured Nginx as a reverse proxy and secured the site with Let's Encrypt SSL certificates, providing a free domain name provided by our manager. Ensured secure, efficient traffic handling for a publicly accessible OCR platform.",
                image: samskritaImg2
            },
            {
                title: "Modular OCR API Design and Code Optimization",
                description: "Designed a modular OCR API framework using OOP principles. The OCRFactory and OCRService classes abstract provider logic, supporting Google Vision and Azure Computer Vision with minimal code changes. Added detailed error handling and logging to support future co-op students joining the project.",
                image: samskritaImg3
            },
            {
                title: "User Experience Enhancements and Testing",
                description: "Proactively engaged external testers to evaluate the platform. Added a 'Select All' button, copy button, and a text-highlight feature allowing users to hover over words to verify detection accuracy. Resolved flickering highlight issues and CSS conflicts in dark mode.",
                image: samskritaImg4
            },
            {
                title: "Invitation System Implementation",
                description: "Proposed and developed an invitation-based access control system using Auth0 and DynamoDB. Users generate 5-digit codes to invite others, with usage tracked to adjust image upload limits. The InviteModal component in OCRPage.js provides a smooth invite-code UX.",
                image: samskritaImg5
            }
        ]
    },
    {
        id: 3,
        title: "President – Computer Club & ArrowHack Founder",
        company: "Fletcher's Meadow Secondary School",
        period: "Sep 2023 – Jun 2024",
        location: "Brampton, Ontario, Canada",
        description: "Led a 30+ member Computer Club focused on teaching Python and preparing students for competitions. Founded and directed ArrowHack — the school's first hackathon — managing every aspect from administrative approval to judging, creating a milestone STEM event for the school.",
        skills: ["Python (Programming Language)", "Public Speaking", "Algorithms", "Embedded Systems", "Event Planning", "Delegation", "Time Management", "Collaborative Leadership"],
        isProject: true,
        sections: [
            {
                title: "Computer Club Leadership",
                description: "As President of the Computer Club, I led a community of 30+ students ranging from absolute beginners learning Python to advanced coders preparing for competitions. Weekly meetings fostered a collaborative environment where students could develop technical skills, share knowledge, and build lasting connections through their shared interest in computer science.",
                image: computerClubImg
            },
            {
                title: "ArrowHack: Our School's First Hackathon",
                description: "Founded and directed ArrowHack — Fletcher's Meadow's inaugural hackathon. Coordinated all aspects: securing administrative approval, organizing judging, and managing the event day. Teams tackled real-world challenges collaboratively, showcasing technical skills, teamwork, and presentation ability. The event became a defining milestone in the school's STEM education journey.",
                image: hackathonImg
            }
        ]
    },
    {
        id: 8,
        title: "Data Entry Specialist",
        company: "Accurate Heart Diagnostic Centre · Co-op",
        period: "Oct 2022 – Jan 2023",
        location: "Brampton, Ontario, Canada",
        description: "Supported clinical operations at a cardiac diagnostic centre across multiple clinic locations. Responsible for downloading Holter Monitor recordings, verifying patient ECG files, troubleshooting database errors and data retrieval issues, resolving technical problems with printers, fax machines, and copiers, and supporting front desk patient interactions and call handling.",
        skills: ["Holter Monitor", "Record Keeping", "Customer Support", "Attention to Detail", "Organization Skills", "Multitasking", "Database Troubleshooting", "Healthcare Operations"],
        isProject: true,
        sections: [
            {
                title: "Holter Monitor Downloads & ECG Data Verification",
                description: "Worked directly with Holter Monitor recordings — downloading cardiac telemetry data and verifying patient ECG files for accuracy across multiple clinic locations. This required precision and attention to clinical data integrity standards, as these records feed directly into physician diagnoses for cardiac patients.",
                image: accurateHeart1
            },
            {
                title: "Clinical Operations & Technical Support",
                description: "Handled day-to-day technical issues including database errors, data retrieval problems, and equipment failures (printers, fax machines, copiers essential to clinic operations). Also maintained the Stress Echo Room, organized packages, managed end-of-day waste protocols, and supported front desk with patient interaction, call handling, and document shredding.",
                image: accurateHeart2
            }
        ]
    }
];

const normalizeSkillKey = (skillName = '') => {
    return skillName
        .toLowerCase()
        .replace(/c\+\+/g, 'cplusplus')
        .replace(/\(.*?\)/g, '')
        .replace(/[^a-z0-9]/g, '')
        .trim();
};

const skillLookup = technicalSkills.reduce((map, skill) => {
    map[normalizeSkillKey(skill.name)] = skill;
    return map;
}, {});

// ── Projects ────────────────────────────────────────────────────────────────
const projectSummaries = [
    {
        id: 'whaleguard-narw-sdm',
        title: 'WhaleGuard – NARW Species Distribution Model',
        period: 'May 2026 – Present',
        technologies: 'Python, XGBoost, Random Forest, scikit-learn, Pandas, Streamlit, OPeNDAP, SHAP, BayesSearchCV, NumPy',
        summary: 'Production-grade ML pipeline predicting North Atlantic Right Whale habitat from satellite-derived oceanographic data. Achieved 0.91 ROC-AUC and ≥80% recall at conservation-optimized thresholds across 64,900 training samples — shifting maritime safety from reactive detection to proactive vessel rerouting.',
        category: "Data Science & Machine Learning",
        previewImage: rbcBorealisImg2,
        githubUrl: 'https://github.com/ParthB21/WhaleGuard_RBC_LSI'
    },
    {
        id: 'subtrackr-subscription-tracker',
        title: 'SubTrackr: Subscription Tracking Web App',
        period: 'May 2025',
        technologies: 'Angular 19, TypeScript, Node.js, Express, MySQL, Plaid API, Chart.js, Angular Material',
        summary: 'Full-stack subscription manager with Plaid API bank transaction analysis, automated recurring charge detection, and Chart.js spending dashboards. Built with Angular 19 + TypeScript frontend and Express + MySQL backend.',
        category: "Web Development & Full Stack",
        previewImage: subscriptionsPageImgPreview,
        demoVideo: 'https://www.youtube.com/embed/nC6cU7PK5vg'
    },
    {
        id: 'quickpoll-vue-realtime-polling',
        title: 'QuickPoll – Real-Time Polling Application',
        period: 'April 2025',
        technologies: 'Vue 3, TypeScript, Vite, Pinia, Socket.IO, Jest, Docker, GitHub Actions',
        summary: 'Live real-time polling app — create polls, vote, and watch results update instantly across all connected devices via Socket.IO. Deployed live on GitHub Pages, containerized with Docker, and CI/CD-automated via GitHub Actions.',
        category: "Web Development & Full Stack",
        previewImage: quickPollPreviewImg,
        liveUrl: 'https://yeehawmcfly.github.io/QuickPoll/',
        isLive: true,
        demoVideo: 'https://www.youtube.com/embed/DdcMO2vwico'
    },
    {
        id: 'photofx-hybrid-mobile-app',
        title: 'PhotoFX – Hybrid Mobile App with Cordova',
        period: 'March 2025',
        technologies: 'HTML5, CSS3, JavaScript, Apache Cordova, Android Studio, Gradle, Android SDK',
        summary: 'Hybrid mobile application compiled to a native Android .apk via Apache Cordova. Configured the full Android SDK toolchain from scratch, implemented real-time CSS photo filters, and integrated native device features (vibration) through Cordova plugins.',
        category: "Mobile Development",
        previewImage: preview,
        demoVideo: 'https://www.youtube.com/embed/tSqHB83lOhY'
    },
    {
        id: 'stm32-ecg-signal-transmission',
        title: 'STM32 ECG Signal Transmission Prototype',
        period: 'October 2024 – November 2024',
        technologies: 'C, STM32CubeIDE, STM32 HAL, ADC, UART, I2C, TEA Encryption, SSD1306 OLED, PuTTY, GitHub',
        summary: 'Dual-MCU secure signal system: 12-bit ADC potentiometer sampling, TEA-encrypted 128-bit UART transmission over a single half-duplex wire, and synchronized SSD1306 OLED visualization on both STM32 boards — verified end-to-end with multimeter accuracy checks.',
        category: "Hardware & Embedded Systems",
        previewImage: finalSystemImgPreview,
        demoVideo: 'https://www.youtube.com/embed/X9aGWWodkJ4'
    },
    {
        id: 'custom-dancing-robot',
        title: 'Custom Dancing Robot',
        period: 'May 2024 – June 2024',
        technologies: 'C++, AutoCAD, Soldering, Servos, PCB Design, Arduino, Arduino IDE, GitHub',
        summary: 'Custom PCB designed and etched from raw copper, 5-servo choreography synced to buzzer music, dual OLED proximity animations, and acrylic chassis cut by hand — fully assembled and performing in three weeks.',
        category: "Hardware & Embedded Systems",
        previewImage: paperImgPreview,
        demoVideo: 'https://www.youtube.com/embed/C6aB4md0lp8'
    },
    {
        id: 'finger-detection-opencv',
        title: 'Finger Detection with OpenCV and PyTorch',
        period: 'January 2025',
        technologies: 'Python, OpenCV, PyTorch, Jupyter Notebook, Computer Vision, Neural Networks, Machine Learning',
        summary: 'Built a gesture classification pipeline end-to-end: captured a private custom dataset with a webcam, trained a PyTorch neural network on 6 finger-count classes, and deployed real-time video inference with OpenCV.',
        category: "Data Science & Machine Learning",
        previewImage: captureDataImgPreview
    },
];

const BOARD_WIDTH = 2600;
const BOARD_HEIGHT = 1800;
const DEFAULT_TILT = { x: 24, y: -18 };

const categoryAnchors = {
    Programming: { x: 520, y: 460 },
    Frontend: { x: 940, y: 1000 },
    Backend: { x: 2020, y: 520 },
    Cloud: { x: 2420, y: 420 },
    DevOps: { x: 2360, y: 1380 },
    'Data Science': { x: 1380, y: 420 },
    'Data Science & Machine Learning': { x: 1380, y: 420 },
    'Machine Learning': { x: 1380, y: 420 },
    Hardware: { x: 1440, y: 1540 },
    Tools: { x: 700, y: 1440 }
};

const boardNavConfig = [
    { key: 'All', label: 'Reset View', helper: 'Show everything' },
    { key: 'Programming', label: 'Programming Core', helper: 'C, C++, JS/TS' },
    { key: 'Frontend', label: 'Frontend Surface', helper: 'UI frameworks' },
    { key: 'Backend+Cloud', label: 'Backend + Cloud', helper: 'APIs & infra' },
    { key: 'DataHub', label: 'Data & ML', helper: 'Analytics + models' },
    { key: 'DevOps', label: 'DevOps Lane', helper: 'Automation stack' },
    { key: 'Hardware', label: 'Hardware Deck', helper: 'MCUs & fabrication' },
    { key: 'Tools', label: 'Tooling Stack', helper: 'Build + testing' }
];

const boardWaypoints = {
    All: { x: BOARD_WIDTH / 2, y: BOARD_HEIGHT / 2, tilt: DEFAULT_TILT },
    Programming: { x: 580, y: 520, tilt: { x: 24, y: -18 } },
    Frontend: { x: 940, y: 1000, tilt: { x: 24, y: -18 } },
    'Backend+Cloud': { x: 2220, y: 470, tilt: { x: 24, y: -18 } },
    DataHub: { x: 1380, y: 420, tilt: { x: 24, y: -18 } },
    DevOps: { x: 2360, y: 1380, tilt: { x: 24, y: -18 } },
    Hardware: { x: 1440, y: 1540, tilt: { x: 24, y: -18 } },
    Tools: { x: 700, y: 1440, tilt: { x: 24, y: -18 } }
};

const categoryFiltersMap = {
    Programming: ['Programming'],
    Frontend: ['Frontend'],
    'Backend+Cloud': ['Backend', 'Cloud'],
    DataHub: ['Data Science', 'Data Science & Machine Learning', 'Machine Learning'],
    DevOps: ['DevOps'],
    Hardware: ['Hardware'],
    Tools: ['Tools'],
    All: null
};

const fallbackAnchor = { x: BOARD_WIDTH / 2, y: BOARD_HEIGHT / 2 };

const Home = () => {
    const navigate = useNavigate();
    const [expandedProject, setExpandedProject] = useState(null);
    const [expandedExperience, setExpandedExperience] = useState(null);
    const [selectedImage, setSelectedImage] = useState(null);
    const [selectedSkill, setSelectedSkill] = useState(null);
    const heroRef = useRef(null);
    const heroProgressRef = useRef(0);
    const heroProgressTargetRef = useRef(0);
    const heroAnimationFrameRef = useRef(null);
    const heroMediaRef = useRef(null);
    const heroMidgroundRef = useRef(null);
    const heroOverlayRef = useRef(null);
    const heroScrollCueRef = useRef(null);
    const skillsViewportRef = useRef(null);
    const [viewportSize, setViewportSize] = useState({ width: 0, height: 0 });
    const [boardPosition, setBoardPosition] = useState({ x: BOARD_WIDTH / 2, y: BOARD_HEIGHT / 2 });
    const boardPositionRef = useRef({ x: BOARD_WIDTH / 2, y: BOARD_HEIGHT / 2 });
    const [boardTilt, setBoardTilt] = useState(DEFAULT_TILT);
    const dragStateRef = useRef({
        active: false,
        pointerId: null,
        startPointer: { x: 0, y: 0 },
        startPosition: { x: BOARD_WIDTH / 2, y: BOARD_HEIGHT / 2 }
    });
    const [activeCategory, setActiveCategory] = useState('All');
    const [isAutoNavigating, setIsAutoNavigating] = useState(false);
    const [isDraggingBoard, setIsDraggingBoard] = useState(false);
    const autoNavTimeoutRef = useRef(null);

    const skillNodes = useMemo(() => {
        const nodesPerRing = 8;
        const ringSpacing = 110;
        const baseRadius = 170;

        const groupedSkills = technicalSkills.reduce((acc, skill, originalIndex) => {
            const categories = (skill.category || 'General').split(',').map(c => c.trim());
            categories.forEach(key => {
                if (!acc[key]) acc[key] = [];
                acc[key].push({ skill, originalIndex });
            });
            return acc;
        }, {});

        const backendCloudSkills = [
            ...(groupedSkills['Backend'] || []),
            ...(groupedSkills['Cloud'] || [])
        ];
        if (backendCloudSkills.length > 0) {
            groupedSkills['Backend+Cloud'] = backendCloudSkills;
            delete groupedSkills['Backend'];
            delete groupedSkills['Cloud'];
        }

        const orderedNodes = [];
        Object.entries(groupedSkills).forEach(([categoryKey, entries]) => {
            const anchor = (categoryKey === 'Backend+Cloud' ? boardWaypoints['Backend+Cloud'] : categoryAnchors[categoryKey]) || fallbackAnchor;
            const sortedEntries = entries.slice().sort((a, b) => a.skill.name.localeCompare(b.skill.name));

            for (let ringStart = 0; ringStart < sortedEntries.length; ringStart += nodesPerRing) {
                const ringIndex = Math.floor(ringStart / nodesPerRing);
                const ringNodes = sortedEntries.slice(ringStart, ringStart + nodesPerRing);
                const angleStep = (Math.PI * 2) / ringNodes.length;
                const radius = baseRadius + ringIndex * ringSpacing;
                const angleOffset = ringNodes.length === 1 ? 0 : (ringIndex % 2 ? angleStep / 2 : 0);

                ringNodes.forEach((entry, idx) => {
                    const angle = angleOffset + idx * angleStep;
                    const rawX = anchor.x + Math.cos(angle) * radius;
                    const rawY = anchor.y + Math.sin(angle) * radius;
                    orderedNodes.push({
                        id: `skill-node-${entry.originalIndex}-${normalizeSkillKey(entry.skill.name)}`,
                        skillData: entry.skill,
                        position: {
                            x: Math.max(240, Math.min(BOARD_WIDTH - 240, rawX)),
                            y: Math.max(240, Math.min(BOARD_HEIGHT - 240, rawY))
                        }
                    });
                });
            }
        });
        return orderedNodes;
    }, []);

    const categoriesWithSkills = useMemo(() => {
        const unique = new Set();
        technicalSkills.forEach(skill => unique.add(skill.category || 'General'));
        return Array.from(unique);
    }, []);

    const updateViewportSize = useCallback(() => {
        if (!skillsViewportRef.current) return;
        const rect = skillsViewportRef.current.getBoundingClientRect();
        setViewportSize({ width: rect.width || 1, height: rect.height || 1 });
    }, []);

    useEffect(() => {
        updateViewportSize();
        window.addEventListener('resize', updateViewportSize);
        return () => window.removeEventListener('resize', updateViewportSize);
    }, [updateViewportSize]);

    const clampBoardPosition = useCallback((position) => {
        const halfWidth = Math.max(120, (viewportSize.width || 0) / 2);
        const halfHeight = Math.max(120, (viewportSize.height || 0) / 2);
        return {
            x: Math.min(BOARD_WIDTH - halfWidth, Math.max(halfWidth, position.x)),
            y: Math.min(BOARD_HEIGHT - halfHeight, Math.max(halfHeight, position.y))
        };
    }, [viewportSize]);

    const syncBoardPosition = useCallback((nextPosition) => {
        setBoardPosition(prev => {
            const target = clampBoardPosition(nextPosition || prev);
            boardPositionRef.current = target;
            return target;
        });
    }, [clampBoardPosition]);

    useEffect(() => { syncBoardPosition(boardPositionRef.current); }, [viewportSize, syncBoardPosition]);

    const resetDragState = useCallback(() => {
        dragStateRef.current = {
            active: false, pointerId: null,
            startPointer: { x: 0, y: 0 },
            startPosition: boardPositionRef.current
        };
        setTimeout(() => setBoardTilt(DEFAULT_TILT), 180);
    }, []);

    useEffect(() => {
        return () => { if (autoNavTimeoutRef.current) clearTimeout(autoNavTimeoutRef.current); };
    }, []);

    const handleBoardPointerDown = useCallback((event) => {
        if (!skillsViewportRef.current) return;
        const target = event.target;
        if (target && typeof target.closest === 'function' && target.closest('.board-node')) return;
        skillsViewportRef.current.setPointerCapture(event.pointerId);
        dragStateRef.current = {
            active: true, pointerId: event.pointerId,
            startPointer: { x: event.clientX, y: event.clientY },
            startPosition: boardPositionRef.current
        };
        setIsDraggingBoard(true);
        setIsAutoNavigating(false);
        setBoardTilt(DEFAULT_TILT);
        if (autoNavTimeoutRef.current) clearTimeout(autoNavTimeoutRef.current);
    }, []);

    const handleBoardPointerMove = useCallback((event) => {
        const dragState = dragStateRef.current;
        if (!dragState.active || dragState.pointerId !== event.pointerId) return;
        const dx = event.clientX - dragState.startPointer.x;
        const dy = event.clientY - dragState.startPointer.y;
        setBoardPosition(() => {
            const target = clampBoardPosition({ x: dragState.startPosition.x - dx * 1.2, y: dragState.startPosition.y - dy * 1.2 });
            boardPositionRef.current = target;
            return target;
        });
        setBoardTilt(DEFAULT_TILT);
    }, [clampBoardPosition]);

    const handleBoardPointerUp = useCallback(() => {
        const dragState = dragStateRef.current;
        if (dragState.pointerId && skillsViewportRef.current?.hasPointerCapture(dragState.pointerId)) {
            skillsViewportRef.current.releasePointerCapture(dragState.pointerId);
        }
        if (dragState.active) { resetDragState(); setIsDraggingBoard(false); }
    }, [resetDragState]);

    const navigateBoard = useCallback((targetKey) => {
        const waypoint = boardWaypoints[targetKey];
        if (!waypoint) return;
        setActiveCategory(targetKey);
        setIsAutoNavigating(true);
        setIsDraggingBoard(false);
        dragStateRef.current = { active: false, pointerId: null, startPointer: { x: 0, y: 0 }, startPosition: boardPositionRef.current };
        setBoardPosition(() => {
            const target = clampBoardPosition({ x: waypoint.x, y: waypoint.y });
            boardPositionRef.current = target;
            return target;
        });
        setBoardTilt(DEFAULT_TILT);
        if (autoNavTimeoutRef.current) clearTimeout(autoNavTimeoutRef.current);
        autoNavTimeoutRef.current = setTimeout(() => { setIsAutoNavigating(false); setBoardTilt(DEFAULT_TILT); }, 1600);
    }, [clampBoardPosition]);

    const handleViewportKeyDown = useCallback((event) => {
        const isPrevKey = event.key === '[' || event.key === '{';
        const isNextKey = event.key === ']' || event.key === '}';
        if (isPrevKey || isNextKey) {
            event.preventDefault();
            const orderedKeys = boardNavConfig.map(item => item.key);
            const currentIndex = Math.max(0, orderedKeys.indexOf(activeCategory));
            const delta = isPrevKey ? -1 : 1;
            navigateBoard(orderedKeys[(currentIndex + delta + orderedKeys.length) % orderedKeys.length]);
            return;
        }
        const step = event.shiftKey ? 140 : 70;
        const keyMap = { ArrowUp: { dx: 0, dy: -step }, ArrowDown: { dx: 0, dy: step }, ArrowLeft: { dx: -step, dy: 0 }, ArrowRight: { dx: step, dy: 0 } };
        if (!keyMap[event.key]) return;
        event.preventDefault();
        setBoardPosition(prev => {
            const target = clampBoardPosition({ x: prev.x + keyMap[event.key].dx, y: prev.y + keyMap[event.key].dy });
            boardPositionRef.current = target;
            return target;
        });
        setIsAutoNavigating(false);
        setBoardTilt(DEFAULT_TILT);
    }, [activeCategory, clampBoardPosition, navigateBoard]);

    useEffect(() => {
        const handleScroll = () => {
            const heroHeight = heroRef.current?.offsetHeight || window.innerHeight || 1;
            const scrollTop = window.scrollY || 0;
            heroProgressTargetRef.current = Math.min(1, scrollTop / heroHeight);
            // Restart animation loop if it was stopped
            if (!heroAnimationFrameRef.current) {
                heroAnimationFrameRef.current = requestAnimationFrame(animateHero);
            }
        };
        const applyHeroStyles = (p) => {
            if (heroMediaRef.current) {
                heroMediaRef.current.style.transform = `translateY(${p * 120}px) scale(${1 + p * 0.08})`;
                heroMediaRef.current.style.opacity = Math.max(0.18, 1 - p * 0.75);
            }
            if (heroMidgroundRef.current) {
                heroMidgroundRef.current.style.transform = `translateY(${p * 55}px)`;
            }
            if (heroOverlayRef.current) {
                heroOverlayRef.current.style.opacity = Math.max(0.26, 1 - p * 0.55);
                heroOverlayRef.current.style.transform = `translateY(${p * 18}px)`;
            }
            if (heroScrollCueRef.current) {
                heroScrollCueRef.current.style.opacity = Math.max(0, 1 - p * 3);
            }
        };
        const animateHero = () => {
            const prev = heroProgressRef.current;
            const target = heroProgressTargetRef.current;
            const next = prev + (target - prev) * 0.12;
            // Stop the loop when the value has settled (< 0.0005 difference)
            if (Math.abs(next - target) < 0.0005) {
                heroProgressRef.current = target;
                applyHeroStyles(target);
                heroAnimationFrameRef.current = null;
                return;
            }
            heroProgressRef.current = next;
            applyHeroStyles(next);
            heroAnimationFrameRef.current = requestAnimationFrame(animateHero);
        };
        window.addEventListener('scroll', handleScroll, { passive: true });
        handleScroll();
        heroAnimationFrameRef.current = requestAnimationFrame(animateHero);
        return () => {
            window.removeEventListener('scroll', handleScroll);
            if (heroAnimationFrameRef.current) cancelAnimationFrame(heroAnimationFrameRef.current);
        };
    }, []);

    // ── Scroll-reveal IntersectionObserver ──
    useEffect(() => {
        const revealElements = document.querySelectorAll('.reveal-on-scroll');
        if (!revealElements.length) return;
        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach(entry => {
                    if (entry.isIntersecting) {
                        entry.target.classList.add('revealed');
                        observer.unobserve(entry.target);
                    }
                });
            },
            { threshold: 0.08, rootMargin: '0px 0px -60px 0px' }
        );
        revealElements.forEach(el => observer.observe(el));
        return () => observer.disconnect();
    }, []);

    // ── Generate stable particle positions for midground parallax ──
    const particles = useMemo(() => {
        const seed = [0.12, 0.87, 0.34, 0.56, 0.91, 0.23, 0.67, 0.45, 0.78, 0.09,
                      0.53, 0.71, 0.38, 0.62, 0.15, 0.84, 0.29, 0.96, 0.41, 0.73];
        return seed.map((s, i) => ({
            left: `${(s * 100).toFixed(1)}%`,
            top: `${((seed[(i + 7) % 20]) * 100).toFixed(1)}%`,
            size: 3 + Math.floor(s * 6),
            delay: `${(i * 0.4).toFixed(1)}s`
        }));
    }, []);

    const openImage = (img) => setSelectedImage(img);
    const closeImage = () => setSelectedImage(null);

    const scrollToSection = (sectionId) => {
        const element = document.getElementById(sectionId);
        if (element) element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    };

    useEffect(() => {
        const handleEscKey = (event) => { if (event.key === 'Escape' && selectedImage) closeImage(); };
        if (selectedImage) {
            document.addEventListener('keydown', handleEscKey);
            document.body.style.overflow = 'hidden';
        }
        return () => { document.removeEventListener('keydown', handleEscKey); document.body.style.overflow = 'unset'; };
    }, [selectedImage]);

    const handleLearnMore = (projectId) => {
        if (selectedSkill) closeSkillModal();
        navigate(`/projects/${projectId}`);
        setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 100);
    };

    const handleExpandExperience = (expId, event) => {
        if (event) { event.preventDefault(); event.stopPropagation(); }
        if (selectedSkill) closeSkillModal();
        const shouldExpand = expandedExperience !== expId;
        setExpandedExperience(shouldExpand ? expId : null);
        if (shouldExpand) {
            setTimeout(() => {
                const expElement = document.getElementById(`exp-${expId}`);
                if (expElement) {
                    const currentScrollPos = window.scrollY;
                    const elementPosition = expElement.getBoundingClientRect().top + currentScrollPos;
                    window.scrollTo({ top: elementPosition - 120, behavior: 'smooth' });
                }
            }, 100);
        }
    };

    const handleSkillClick = (skillName, skillLevel) => {
        const normalizedSkillKey = normalizeSkillKey(skillName);
        const coreSkillName = skillName.toLowerCase().includes('(')
            ? skillName.toLowerCase().substring(0, skillName.toLowerCase().indexOf('(')).trim()
            : skillName.toLowerCase();
        const normalizedCoreSkillKey = normalizeSkillKey(coreSkillName);
        const skillEntry = skillLookup[normalizedSkillKey] || skillLookup[normalizedCoreSkillKey];
        if (!skillEntry) return;

        const relatedProjects = (skillEntry?.projectIds || [])
            .map(projectId => projectSummaries.find(project => project.id === projectId))
            .filter(Boolean);
        const relatedExperiences = (skillEntry?.experienceIds || [])
            .map(expId => experiences.find(exp => exp.id === expId))
            .filter(Boolean);

        setSelectedSkill({ name: skillName, level: skillLevel, projects: relatedProjects, experiences: relatedExperiences });
    };

    const closeSkillModal = () => setSelectedSkill(null);

    const toggleProjectExpansion = (projectId, forceExpand = false) => {
        if (selectedSkill) closeSkillModal();
        const shouldExpand = forceExpand || (expandedProject !== projectId);
        const newExpandedProject = shouldExpand ? projectId : null;
        setExpandedProject(newExpandedProject);
        if (shouldExpand) {
            setTimeout(() => {
                const projectElement = document.getElementById(`project-${projectId}`);
                if (projectElement) {
                    const elementPosition = projectElement.getBoundingClientRect().top + window.scrollY;
                    window.scrollTo({ top: elementPosition - 100, behavior: 'smooth' });
                    projectElement.style.boxShadow = '0 0 30px rgba(6, 182, 212, 0.25)';
                    projectElement.style.borderColor = 'rgba(6, 182, 212, 0.4)';
                    projectElement.style.transition = 'all 0.3s ease';
                    setTimeout(() => {
                        if (projectElement && document.contains(projectElement)) {
                            projectElement.style.boxShadow = '';
                            projectElement.style.borderColor = '';
                        }
                    }, 3000);
                }
            }, 200);
        }
    };

    const groupedProjects = projectSummaries.reduce((acc, project) => {
        const category = project.category || 'Other Projects';
        if (!acc[category]) acc[category] = [];
        acc[category].push(project);
        return acc;
    }, {});

    const categoryOrder = [
        "Data Science & Machine Learning",
        "Web Development & Full Stack",
        "Mobile Development",
        "Hardware & Embedded Systems",
        "Other Projects"
    ];

    const projectCategoryDescriptions = {
        "Data Science & Machine Learning": "Research-grade ML pipelines and computer vision systems built for real-world impact.",
        "Web Development & Full Stack": "End-to-end product builds where I owned UX, APIs, and deployment.",
        "Mobile Development": "Hybrid mobile releases shipped as installable native experiences.",
        "Hardware & Embedded Systems": "Cross-discipline builds stitching firmware, sensors, and custom enclosure design.",
        "Other Projects": "Extra builds that don't fit the above labels but still show craft."
    };

    const orderedProjectGroups = [];
    categoryOrder.forEach(category => {
        if (groupedProjects[category]) {
            orderedProjectGroups.push({
                name: category,
                description: projectCategoryDescriptions[category],
                projects: groupedProjects[category]
            });
        }
    });
    Object.entries(groupedProjects)
        .filter(([category]) => !categoryOrder.includes(category))
        .forEach(([category, projects]) => {
            orderedProjectGroups.push({ name: category, description: projectCategoryDescriptions[category] || '', projects });
        });

    const activeFilters = categoryFiltersMap[activeCategory];
    const activeWaypointMeta = boardNavConfig.find(item => item.key === activeCategory);
    const boardTranslateX = (viewportSize.width || 0) / 2 - boardPosition.x;
    const boardTranslateY = (viewportSize.height || 0) / 2 - boardPosition.y;
    const boardSpokes = categoriesWithSkills
        .filter(category => categoryAnchors[category])
        .filter(category => category !== 'Backend' && category !== 'Cloud')
        .map(category => {
            const anchor = categoryAnchors[category];
            const dx = anchor.x - fallbackAnchor.x;
            const dy = anchor.y - fallbackAnchor.y;
            return { category, length: Math.sqrt(dx * dx + dy * dy), angle: Math.atan2(dy, dx) };
        });

    const hasBackendOrCloud = categoriesWithSkills.some(cat => cat === 'Backend' || cat === 'Cloud');
    if (hasBackendOrCloud && boardWaypoints['Backend+Cloud']) {
        const anchor = boardWaypoints['Backend+Cloud'];
        const dx = anchor.x - fallbackAnchor.x;
        const dy = anchor.y - fallbackAnchor.y;
        boardSpokes.push({ category: 'Backend+Cloud', length: Math.sqrt(dx * dx + dy * dy), angle: Math.atan2(dy, dx) });
    }

    return (
        <div className="home-page page-enter">
            {/* ── HERO ─────────────────────────────────────────────────────── */}
            <section className="parallax-hero" ref={heroRef}>
                {/* Layer 1: Background — moves slowest (furthest away) */}
                <div
                    ref={heroMediaRef}
                    className="parallax-hero__media"
                    style={{ backgroundImage: `url(${waterlooHero})` }}
                ></div>

                {/* Layer 2: Midground — floating particles, moves at medium speed */}
                <div
                    ref={heroMidgroundRef}
                    className="parallax-hero__midground"
                >
                    {particles.map((p, i) => (
                        <div
                            key={i}
                            className="particle"
                            style={{
                                left: p.left,
                                top: p.top,
                                width: p.size,
                                height: p.size,
                                animationDelay: p.delay
                            }}
                        />
                    ))}
                </div>

                {/* Layer 3: Foreground — content, moves slowest / stays put */}
                <div
                    ref={heroOverlayRef}
                    className="parallax-hero__overlay"
                >
                    <div className="hero-content">
                        <div className="hero-text">
                            <span className="hero-eyebrow">Computer Engineering · University of Waterloo</span>
                            <h1>Engineering systems that move data, protect wildlife, and ship products</h1>
                            <p className="hero-summary">
                                From Samsung data pipelines preventing millions in telecom overbilling to endangered whale ML models — I build software that solves real problems at production scale.
                            </p>
                            <div className="hero-cta">
                                <button onClick={() => scrollToSection('projects-showcase')}>Explore Projects</button>
                                <button className="ghost" onClick={() => scrollToSection('experience')}>
                                    View Experience
                                </button>
                            </div>
                            <div className="hero-socials">
                                <a href="https://github.com/YeehawMcfly" target="_blank" rel="noopener noreferrer" aria-label="GitHub">
                                    <FontAwesomeIcon icon={faGithub} />
                                </a>
                                <a href="https://www.linkedin.com/in/vishnu-ajit-a56760283/" target="_blank" rel="noopener noreferrer" aria-label="LinkedIn">
                                    <FontAwesomeIcon icon={faLinkedin} />
                                </a>
                                <a href="mailto:vajit@uwaterloo.ca" aria-label="Email">
                                    <FontAwesomeIcon icon={faEnvelope} />
                                </a>
                            </div>
                        </div>
                        <div className="hero-profile">
                            <div className="hero-profile-image">
                                <img src={profilePic} alt="Vishnu Ajit" />
                            </div>
                            <div className="hero-profile-info">
                                <h2>Vishnu Ajit</h2>
                                <p>Computer Engineering</p>
                                <p className="university">University of Waterloo</p>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="hero-scroll-cue" ref={heroScrollCueRef}>
                    <span>Scroll</span>
                </div>
            </section>

            <div className="home-container">

                {/* ── PROJECTS ─────────────────────────────────────────────── */}
                <section className="project-showcase reveal-on-scroll" id="projects-showcase">
                    <div className="section-heading">
                        <span className="eyebrow">Build Log</span>
                        <h2>Projects that balance UX and engineering depth</h2>
                        <p>Cards expand into mini case studies. Click any card to explore or hit "View case study" for the full deep-dive.</p>
                    </div>
                    {orderedProjectGroups.map(group => (
                        <div className="project-category" key={group.name}>
                            <div className="project-category__header">
                                <p className="project-category__eyebrow">{group.name}</p>
                                {group.description && <p className="project-category__summary">{group.description}</p>}
                            </div>
                            <div className="project-showcase-grid">
                                {group.projects.map(project => (
                                    <article
                                        key={project.id}
                                        id={`project-${project.id}`}
                                        className={`project-card ${expandedProject === project.id ? 'is-active' : ''} ${project.isLive ? 'has-live-site' : ''}`}
                                        onClick={(e) => { e.preventDefault(); e.stopPropagation(); toggleProjectExpansion(project.id, false); }}
                                    >
                                        {/* Preview image */}
                                        {project.previewImage && (
                                            <div className="project-card__preview">
                                                <img src={project.previewImage} alt={project.title} />
                                                {project.demoVideo && (
                                                    <div className="project-card__video-badge">
                                                        <FontAwesomeIcon icon={faPlay} />
                                                        <span>Watch Demo</span>
                                                    </div>
                                                )}
                                                {project.isLive && (
                                                    <div className="project-card__live-badge">
                                                        <span className="live-dot"></span>
                                                        Live
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                        <div className="project-card__body">
                                            <div className="project-card__meta">
                                                <span className="project-card__tag">{project.category || 'Project'}</span>
                                                <span className="project-card__period">{project.period}</span>
                                            </div>
                                            <h3>{project.title}</h3>
                                            <p className="project-card__summary">{project.summary}</p>
                                            <div className="project-tech-chips">
                                                {project.technologies.split(', ').slice(0, 4).map(tech => (
                                                    <span className="tech-chip tech-name" key={tech} onClick={(e) => { e.stopPropagation(); handleSkillClick(tech); }}>{tech}</span>
                                                ))}
                                            </div>
                                            {project.liveUrl && (
                                                <a href={project.liveUrl} className="project-card__live-link" target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()}>
                                                    Live Site <FontAwesomeIcon icon={faArrowRight} />
                                                </a>
                                            )}
                                            {project.githubUrl && (
                                                <a href={project.githubUrl} className="project-card__live-link" target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()}>
                                                    GitHub <FontAwesomeIcon icon={faGithub} />
                                                </a>
                                            )}
                                        </div>

                                        {expandedProject === project.id && (
                                            <div className="project-card__detail">
                                                <div className="stage-stack">
                                                    {project.id === 'whaleguard-narw-sdm' && (
                                                        <>
                                                            <div className="stage-box"><h4>The Problem — 380 Whales Remaining</h4><p>Ship strikes are a primary cause of NARW mortality. Existing systems react only after whales enter shipping lanes. WhaleGuard predicts habitat from satellite oceanographic data to enable proactive route changes.</p></div>
                                                            <div className="stage-box"><h4>ETL Pipeline & Feature Engineering</h4><p>4-phase pipeline: pseudo-absence generation (Gowan 2014 method), OPeNDAP satellite extraction, 10-feature engineering (SST gradients, chlorophyll, bathymetry), and model training on 64,900 samples.</p></div>
                                                            <div className="stage-box"><h4>Model Training & Threshold Optimization</h4><p>Random Forest (0.91 ROC-AUC) vs XGBoost (0.90 ROC-AUC) via BayesSearchCV. Conservation-optimized thresholds (RF τ = 0.15) achieve ≥80% recall — prioritizing whale detection above false-positive minimization.</p></div>
                                                            <div className="stage-box"><h4>Streamlit Dashboard</h4><p>Interactive Gulf of St. Lawrence habitat probability heatmap with live parameter queries. Presented to RBC Borealis mentors and CIFAR researchers at the program's final showcase.</p></div>
                                                        </>
                                                    )}
                                                    {project.id === 'custom-dancing-robot' && (
                                                        <>
                                                            <div className="stage-box"><h4>Distance Detection & OLED Integration</h4><p>Ultrasonic sensor with real-time dual OLED display for proximity-triggered dance movements and animated GIFs.</p></div>
                                                            <div className="stage-box"><h4>Music & Library Conflict Resolution</h4><p>Integrated passive buzzer for music synchronized to movement. Resolved NewPing / music library vector conflict via alternative distance measurement.</p></div>
                                                            <div className="stage-box"><h4>PCB Design & Fabrication</h4><p>Drafted schematics, drilled copper-clad board, etched with ferric chloride, and verified all traces with a multimeter.</p></div>
                                                            <div className="stage-box"><h4>Physical Assembly & Structural Design</h4><p>Cut acrylic chassis with band saw, drilled metal brackets for servos, spray-painted, and hand-wired all components.</p></div>
                                                            <div className="stage-box"><h4>Servo Calibration & Fine-Tuning</h4><p>Calibrated positional and continuous rotation servos with timing delays to synchronize choreography with music playback.</p></div>
                                                        </>
                                                    )}
                                                    {project.id === 'stm32-ecg-signal-transmission' && (
                                                        <>
                                                            <div className="stage-box"><h4>UART Half-Duplex Communication</h4><p>Half-duplex UART between STM32F446RE and STM32F401RE over a single PA9 wire at 115200 baud with interrupt-driven mode switching.</p></div>
                                                            <div className="stage-box"><h4>Potentiometer & ADC Integration</h4><p>12-bit ADC reading voltage from 10kΩ potentiometer, mapped to OLED y-positions. PuTTY monitoring confirmed ±5% accuracy.</p></div>
                                                            <div className="stage-box"><h4>OLED Signal Visualization</h4><p>SSD1306 OLED via I2C (400kHz) showing scrolling real-time voltage graphs and numerical display mode on both boards.</p></div>
                                                            <div className="stage-box"><h4>TEA Encryption</h4><p>128-bit TEA encryption (32 cycles) secures 8-byte ADC data blocks. Decrypted voltages verified with identical graphs on both STM32s.</p></div>
                                                            <div className="stage-box"><h4>System Integration & Verification</h4><p>Button-toggled display modes, multimeter-verified voltage accuracy, 0.64W power consumption. Full secure transmission over 1 meter.</p></div>
                                                        </>
                                                    )}
                                                    {project.id === 'subtrackr-subscription-tracker' && (
                                                        <>
                                                            <div className="stage-box"><h4>Angular 19 & TypeScript Frontend</h4><p>Responsive SPA with Angular Material components, ng2-charts visualizations, and reactive Angular HttpClient API calls.</p></div>
                                                            <div className="stage-box"><h4>Node.js & MySQL Backend</h4><p>REST API with Express covering subscriptions, payments, and Plaid integration. MySQL schema with node-cron for payment reminders.</p></div>
                                                            <div className="stage-box"><h4>Plaid API Integration</h4><p>Implemented Link token flow, public token exchange, and transaction processing to auto-detect recurring subscription charges from bank accounts.</p></div>
                                                            <div className="stage-box"><h4>Financial Analytics Dashboard</h4><p>Spending-by-category pie charts and monthly trend bar charts powered by Chart.js, connected to real backend aggregation endpoints.</p></div>
                                                        </>
                                                    )}
                                                    {project.id === 'quickpoll-vue-realtime-polling' && (
                                                        <>
                                                            <div className="stage-box"><h4>Vue 3 & TypeScript Frontend</h4><p>SPA with Composition API, Pinia state management, smooth PageTransition.vue animations, and real-time Socket.IO subscriptions in PollList.vue.</p></div>
                                                            <div className="stage-box"><h4>Express & MongoDB Backend</h4><p>JWT authentication, bcrypt password hashing, Mongoose poll validation (min 2 options), and Socket.IO event emission on every vote.</p></div>
                                                            <div className="stage-box"><h4>Real-Time Updates with Socket.IO</h4><p>vote:cast and poll:update events synchronize all connected clients instantly — no refresh needed to see live result changes.</p></div>
                                                            <div className="stage-box"><h4>DevOps & CI/CD</h4><p>Multi-stage Docker build (Vue → Nginx), Docker Compose for full stack, GitHub Actions auto-tests and deploys on merge to main.</p></div>
                                                        </>
                                                    )}
                                                    {project.id === 'photofx-hybrid-mobile-app' && (
                                                        <>
                                                            <div className="stage-box"><h4>Android SDK Toolchain Setup</h4><p>Configured Cordova CLI, Android Studio, Gradle, ANDROID_SDK_ROOT, and PATH from scratch for a full native Android build pipeline.</p></div>
                                                            <div className="stage-box"><h4>Hybrid App & CSS Filters</h4><p>Responsive HTML/CSS/JS UI with real-time grayscale, sepia, and invert photo filters. Smooth CSS transitions between views.</p></div>
                                                            <div className="stage-box"><h4>Native Integration & Testing</h4><p>Wrapped to .apk via Cordova, integrated vibration plugin, debugged on Pixel 6 emulator via Chrome DevTools, tested PWA on iPhone Safari.</p></div>
                                                        </>
                                                    )}
                                                    {project.id === 'finger-detection-opencv' && (
                                                        <>
                                                            <div className="stage-box"><h4>Custom Dataset Creation</h4><p>Captured a private hand-gesture dataset with webcam using OpenCV, organized into labeled folders for 0–5 finger classes.</p></div>
                                                            <div className="stage-box"><h4>PyTorch Neural Network</h4><p>Fully connected network with cross-entropy loss trained on grayscale normalized images. Evaluated on accuracy and average test loss.</p></div>
                                                            <div className="stage-box"><h4>Results & Insights</h4><p>Consistent recognition on simple gestures (0 or 5). Learned the full pipeline from data capture to model inference and deployment.</p></div>
                                                        </>
                                                    )}
                                                </div>
                                            </div>
                                        )}
                                        <div className="project-card__footer">
                                            <button
                                                className="ghost"
                                                onClick={(e) => { e.stopPropagation(); handleLearnMore(project.id); }}
                                            >
                                                View case study <FontAwesomeIcon icon={faArrowRight} />
                                            </button>
                                            <button
                                                className="text-link"
                                                onClick={(e) => { e.stopPropagation(); toggleProjectExpansion(project.id, expandedProject !== project.id); }}
                                            >
                                                {expandedProject === project.id ? 'Collapse' : 'Quick story'}
                                            </button>
                                        </div>
                                    </article>
                                ))}
                            </div>
                        </div>
                    ))}
                </section>

                {/* ── EXPERIENCE TIMELINE ──────────────────────────────────── */}
                <section className="experience-timeline reveal-on-scroll" id="experience">
                    <div className="section-heading">
                        <span className="eyebrow">Career Path</span>
                        <h2>Experience timeline</h2>
                        <p>Co-ops, research programs, and leadership roles — each with the story behind the work.</p>
                    </div>
                    <div className="timeline-track">
                        {experiences.map(exp => (
                            <article
                                key={exp.id}
                                id={`exp-${exp.id}`}
                                className={`timeline-card ${expandedExperience === exp.id ? 'is-open' : ''}`}
                            >
                                <div className="timeline-marker">
                                    <span>{exp.period}</span>
                                </div>
                                <div className="timeline-body">
                                    <header className="timeline-header">
                                        <h3>{exp.title}</h3>
                                        <p>{exp.company} — {exp.location}</p>
                                    </header>
                                    <p className="timeline-description">{exp.description}</p>
                                    <div className="exp-skills">
                                        {exp.skills.map((skill, i) => (
                                            <span className="exp-skill" key={i} onClick={(e) => { e.stopPropagation(); handleSkillClick(skill); }}>
                                                {skill}
                                            </span>
                                        ))}
                                    </div>
                                    {exp.isProject && (
                                        <button
                                            className="timeline-toggle"
                                            onClick={(e) => handleExpandExperience(exp.id, e)}
                                        >
                                            {expandedExperience === exp.id ? 'Hide story' : 'See story'}
                                        </button>
                                    )}
                                    {expandedExperience === exp.id && exp.sections && (
                                        <div className="experience-story-grid">
                                            {exp.sections.map((section, index) => (
                                                <div
                                                    key={section.title}
                                                    className={`story-panel ${index % 2 ? 'reverse' : ''}`}
                                                >
                                                    <div className="story-panel__media">
                                                        <img
                                                            src={section.image}
                                                            alt={section.title}
                                                            onClick={() => openImage(section.image)}
                                                        />
                                                    </div>
                                                    <div className="story-panel__text">
                                                        <h4>{section.title}</h4>
                                                        <p>{section.description}</p>
                                                    </div>
                                                </div>
                                            ))}
                                            {exp.id === 1 && (
                                                <div className="expandable-summary">
                                                    <h4>Technical Achievements</h4>
                                                    <ul className="technical-summary">
                                                        <li>Implemented an Express backend with OCR upload, history, and preference management endpoints.</li>
                                                        <li>Integrated Google Vision and Azure Computer Vision via a modular OOP provider system.</li>
                                                        <li>Deployed to AWS EC2 with Nginx, S3, DynamoDB, and Auth0-powered authentication.</li>
                                                        <li>Designed a React interface with dark mode, interactive text-highlight verification, and invite codes.</li>
                                                    </ul>
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            </article>
                        ))}
                    </div>
                </section>

                {/* ── SKILLS MIND MAP ──────────────────────────────────────── */}
                <section className="mindmap-section reveal-on-scroll" id="skills-mindmap">
                    <div className="section-heading">
                        <span className="eyebrow">Skills Atlas</span>
                        <h2>Explore each skill cluster</h2>
                        <p>Click and drag to pan, tap the quick-jump buttons to re-center a domain, then select any node to see related projects and experience.</p>
                    </div>
                    <div className="skills-explorer">
                        <div className="skills-quick-tips-top">
                            <div className="quick-tips-header">
                                <h3>⚡ Quick Tips</h3>
                                <span className="keyboard-hint">Keyboard shortcuts available</span>
                            </div>
                            <div className="quick-tips-grid">
                                <div className="tip-item"><span className="tip-icon">🖱️</span><strong>Drag</strong> anywhere to pan</div>
                                <div className="tip-item"><span className="tip-icon">🎯</span><strong>Click buttons</strong> below to jump to clusters</div>
                                <div className="tip-item"><span className="tip-icon">💡</span><strong>Select a node</strong> to view projects & experience</div>
                                <div className="tip-item tip-keyboard">
                                    <span className="tip-icon">⌨️</span>
                                    <div><strong>[ / ]</strong> keys cycle clusters<br /><strong>Shift + Arrows</strong> for faster panning</div>
                                </div>
                            </div>
                            <div className="skill-level-indicators">
                                <span className="legend-badge expert">Expert</span>
                                <span className="legend-badge advanced">Advanced</span>
                                <span className="legend-badge intermediate">Intermediate</span>
                            </div>
                        </div>

                        <div className="skills-board">
                            <div className="skills-board__hud">
                                <div>
                                    <p className="hud-label">Navigation mode</p>
                                    <strong>{activeWaypointMeta?.label || 'Free roam'}</strong>
                                    <span>{activeWaypointMeta?.helper || 'Drag to move · press [ or ] to cycle sections.'}</span>
                                </div>
                                <div className={`hud-chip ${isDraggingBoard ? 'is-live' : ''}`}>
                                    {isDraggingBoard ? 'Manual control' : isAutoNavigating ? 'Guided flight' : 'Idle hover'}
                                </div>
                            </div>
                            <div
                                className={`skills-board__viewport ${isDraggingBoard ? 'is-dragging' : ''}`}
                                ref={skillsViewportRef}
                                role="application"
                                aria-label="Interactive skills board"
                                tabIndex={0}
                                onPointerDown={handleBoardPointerDown}
                                onPointerMove={handleBoardPointerMove}
                                onPointerUp={handleBoardPointerUp}
                                onPointerLeave={handleBoardPointerUp}
                                onPointerCancel={handleBoardPointerUp}
                                onKeyDown={handleViewportKeyDown}
                            >
                                <div
                                    className={`skills-board__surface ${isAutoNavigating ? 'is-auto' : ''}`}
                                    style={{
                                        width: `${BOARD_WIDTH}px`,
                                        height: `${BOARD_HEIGHT}px`,
                                        transform: `translate3d(${boardTranslateX}px, ${boardTranslateY}px, 0) rotateX(${boardTilt.x}deg) rotateY(${boardTilt.y}deg)`
                                    }}
                                >
                                    <div className="board-grid board-grid--major"></div>
                                    <div className="board-grid board-grid--minor"></div>
                                    {boardSpokes.map(spoke => (
                                        <span
                                            key={`spoke-${spoke.category}`}
                                            className="board-spoke"
                                            style={{
                                                width: `${spoke.length}px`,
                                                transform: `translate(${fallbackAnchor.x}px, ${fallbackAnchor.y}px) rotate(${spoke.angle}rad)`
                                            }}
                                        ></span>
                                    ))}
                                    {skillNodes.map(node => {
                                        const levelClass = node.skillData.level?.toLowerCase() || 'base';
                                        const isDimmed = activeFilters && !activeFilters.includes(node.skillData.category);
                                        return (
                                            <button
                                                key={node.id}
                                                className={`board-node level-${levelClass} ${isDimmed ? 'is-dimmed' : ''}`}
                                                style={{ left: `${node.position.x}px`, top: `${node.position.y}px` }}
                                                type="button"
                                                onClick={() => handleSkillClick(node.skillData.name, node.skillData.level)}
                                            >
                                                <span className="board-node__label">{node.skillData.name}</span>
                                                <small>{node.skillData.category}</small>
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>
                            <div className="skills-board__nav">
                                {boardNavConfig.map(navItem => (
                                    <button
                                        key={navItem.key}
                                        type="button"
                                        className={`board-nav ${activeCategory === navItem.key ? 'is-active' : ''}`}
                                        onClick={() => navigateBoard(navItem.key)}
                                        aria-pressed={activeCategory === navItem.key}
                                    >
                                        <span>{navItem.label}</span>
                                        <small>{navItem.helper}</small>
                                    </button>
                                ))}
                            </div>
                        </div>
                    </div>
                </section>

            </div>

            {/* ── Fullscreen Image Modal ────────────────────────────────────── */}
            {selectedImage && (
                <div className="fullscreen-image-modal" onClick={closeImage}>
                    <img
                        src={selectedImage}
                        alt="Full size"
                        className="fullscreen-image-content"
                        onClick={(e) => e.stopPropagation()}
                    />
                    <span className="fullscreen-image-close" onClick={closeImage}>×</span>
                </div>
            )}

            {/* ── Skill Detail Modal ────────────────────────────────────────── */}
            {selectedSkill && (
                <div className="skill-modal-overlay" onClick={closeSkillModal}>
                    <div className="skill-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="skill-modal-header">
                            <h2>{selectedSkill.name}</h2>
                            <span className="skill-level-badge" style={{ backgroundColor: getLevelColor(selectedSkill.level) }}>
                                {selectedSkill.level}
                            </span>
                            <button className="skill-modal-close" onClick={closeSkillModal}>×</button>
                        </div>
                        <div className="skill-modal-content">
                            {selectedSkill.projects.length > 0 && (
                                <div className="skill-modal-section">
                                    <h3>Projects using {selectedSkill.name}</h3>
                                    <div className="skill-projects-grid">
                                        {selectedSkill.projects.map(project => (
                                            <div
                                                key={project.id}
                                                className="skill-project-card"
                                                onClick={(e) => {
                                                    e.preventDefault(); e.stopPropagation();
                                                    closeSkillModal();
                                                    setTimeout(() => toggleProjectExpansion(project.id, true), 150);
                                                }}
                                            >
                                                <h4>{project.title}</h4>
                                                <p>{project.summary}</p>
                                                <div className="project-tech-preview">
                                                    {project.technologies.split(', ').slice(0, 3).map(tech => (
                                                        <span key={tech} className="tech-tag">{tech}</span>
                                                    ))}
                                                </div>
                                                <div className="visit-project-indicator">
                                                    <span>View Project Details</span>
                                                    <span className="shine-arrow">→</span>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                            {selectedSkill.experiences.length > 0 && (
                                <div className="skill-modal-section">
                                    <h3>Experience with {selectedSkill.name}</h3>
                                    <div className="skill-experiences-list">
                                        {selectedSkill.experiences.map(exp => (
                                            <div
                                                key={exp.id}
                                                className="skill-experience-card"
                                                onClick={(e) => {
                                                    e.preventDefault(); e.stopPropagation();
                                                    closeSkillModal();
                                                    setTimeout(() => {
                                                        handleExpandExperience(exp.id, null);
                                                        setTimeout(() => {
                                                            const expElement = document.getElementById(`exp-${exp.id}`);
                                                            if (expElement) {
                                                                expElement.style.boxShadow = '0 0 30px rgba(6, 182, 212, 0.25)';
                                                                expElement.style.borderColor = 'rgba(6, 182, 212, 0.4)';
                                                                setTimeout(() => {
                                                                    if (expElement && document.contains(expElement)) {
                                                                        expElement.style.boxShadow = '';
                                                                        expElement.style.borderColor = '';
                                                                    }
                                                                }, 3000);
                                                            }
                                                        }, 200);
                                                    }, 150);
                                                }}
                                            >
                                                <h4>{exp.title}</h4>
                                                <p className="exp-company">{exp.company} • {exp.period}</p>
                                                <p className="exp-description">{exp.description.substring(0, 150)}...</p>
                                                <div className="visit-experience-indicator">
                                                    <span>View Experience Details</span>
                                                    <span className="shine-arrow">→</span>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                            {selectedSkill.projects.length === 0 && selectedSkill.experiences.length === 0 && (
                                <div className="no-content">
                                    <p>No specific projects or experiences found for {selectedSkill.name}, but it's part of my technical toolkit!</p>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Home;